import { IActionContext } from '@msdyn365-commerce/core';
import React from 'react';
import { getDeliveryDateRange } from './estDeliveryDate';
import { AttributeValue, DeliveryOption, ProductDeliveryOptions } from '@msdyn365-commerce/retail-proxy';
import * as Msdyn365 from '@msdyn365-commerce/core';

export enum DeliveryBoxesType {
    PDP = 1,
    Cart
}

// Interfaces for component props
interface IDeliveryTileBaseProps {
    hasDeliveryOption: boolean;
    hasStock: boolean;
    isSelected: boolean;
    onSelectionChange: () => void;
    showLowStockWarning?: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
}

interface IHomeDeliveryTileProps extends IDeliveryTileBaseProps {
    hasCncOption: boolean;
    hasCncStock: boolean;
    deliveryDescription?: string;
    vendorName?: string;
    showOnlyMessage?: boolean;
}

interface IClickCollectTileProps extends IDeliveryTileBaseProps {
    hasHdOption: boolean;
    hasHdStock: boolean;
    showOnlyMessage?: boolean;
}

export const isLowStock = (
    productId: number | undefined,
    productAvailabilities: any[],
    warehouseIdsForFulfillment: string[],
    isPlantCategory: boolean
): boolean => {
    const inventories = productAvailabilities.filter(pa => pa.ProductAvailableQuantity?.ProductId === productId);

    if (isPlantCategory) {
        return inventories.some(
            pa => warehouseIdsForFulfillment.includes(pa.InventLocationId?.toLowerCase()) && pa.StockLevelCode === 'LOW'
        );
    }

    return inventories.some(pa => pa.StockLevelCode === 'LOW');
};

const createDeliveryModeHandler = (
    isHomeDelSelected: boolean,
    isCNCSelected: boolean,
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void
) => () => updateModeOfAddToCart(isHomeDelSelected, isCNCSelected);

/**
 * Helper to extract product attribute value by name
 */
const getAttributeValue = (attributes: { Name?: string; TextValue?: string }[] | undefined, name: string): string | undefined => {
    return attributes?.find(attr => attr.Name?.toLowerCase() === name.toLowerCase())?.TextValue;
};

/**
 * Input data for generating add to cart boxes
 */
export interface IAddToCartBoxesInput {
    type: DeliveryBoxesType;
    isHomeDelSelected: boolean;
    isCNCSelected: boolean;
    /** Whether Home Delivery option exists for this product */
    hasHomeDelOption: boolean;
    /** Whether Click & Collect option exists for this product */
    hasCncOption: boolean;
    /** Whether stock is available for Home Delivery */
    hasHomeDelStock: boolean;
    /** Whether stock is available for Click & Collect */
    hasCncStock: boolean;
    actionContext: IActionContext;
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void;
    /** Pre-fetched delivery options from props.data.productDeliveryOptions.result?.deliveryOptions */
    deliveryOptions?: ProductDeliveryOptions[];
    /** Pre-fetched product attributes from props.data.productAttributes.result */
    productAttributes?: AttributeValue[];
    isHomeDelLowStock?: boolean;
    isCNCLowStock?: boolean;
    /** Whether data is still loading - tiles will render in loading state */
    isLoading?: boolean;
}

export const getAddToCartBoxes = (input: IAddToCartBoxesInput): (JSX.Element[] | undefined)[] => {
    const {
        type,
        isHomeDelSelected,
        isCNCSelected,
        hasHomeDelOption,
        hasCncOption,
        hasHomeDelStock,
        hasCncStock,
        actionContext,
        updateModeOfAddToCart,
        deliveryOptions,
        productAttributes,
        isHomeDelLowStock,
        isCNCLowStock,
        isLoading
    } = input;

    if (type === DeliveryBoxesType.PDP) {
        return getAddToCartBoxesForPDP({
            isHomeDelSelected,
            isCNCSelected,
            hasHomeDelOption,
            hasCncOption,
            hasHomeDelStock,
            hasCncStock,
            actionContext,
            updateModeOfAddToCart,
            deliveryOptions,
            productAttributes,
            isHomeDelLowStock,
            isCNCLowStock,
            isLoading
        });
    }

    return [];
};

/**
 * Input for PDP add to cart boxes
 */
interface IGetAddToCartBoxesForPDPInput {
    isHomeDelSelected: boolean;
    isCNCSelected: boolean;
    hasHomeDelOption: boolean;
    hasCncOption: boolean;
    hasHomeDelStock: boolean;
    hasCncStock: boolean;
    actionContext: IActionContext;
    updateModeOfAddToCart: (homeDelSelected: boolean, cncSelected: boolean) => void;
    deliveryOptions?: ProductDeliveryOptions[];
    productAttributes?: AttributeValue[];
    isHomeDelLowStock?: boolean;
    isCNCLowStock?: boolean;
    isLoading?: boolean;
}

const getAddToCartBoxesForPDP = (input: IGetAddToCartBoxesForPDPInput): (JSX.Element[] | undefined)[] => {
    const {
        isHomeDelSelected,
        isCNCSelected,
        hasHomeDelOption,
        hasCncOption,
        hasHomeDelStock,
        hasCncStock,
        actionContext,
        updateModeOfAddToCart,
        deliveryOptions,
        productAttributes,
        isHomeDelLowStock,
        isCNCLowStock,
        isLoading
    } = input;

    // Extract values from pre-fetched data
    const vendorName = getAttributeValue(productAttributes, 'vendorname');
    const configDeliveryModes: string[] = actionContext.requestContext.app.config.deliveryModes || [];

    const hasDeliveryOptions = deliveryOptions && deliveryOptions.length && deliveryOptions.length > 0;
    const firstDeliveryOpt = hasDeliveryOptions ? deliveryOptions![0] : null;

    // Find the home delivery option description if available
    const homeDelOption = firstDeliveryOpt?.DeliveryOptions?.find(opt => configDeliveryModes.includes(opt.Code || ''));
    const deliveryDescription = homeDelOption?.Description;

    const tiles: JSX.Element[] = [];

    // During loading: show both tiles to prevent layout shift
    if (isLoading) {
        tiles.push(
            <HomeDeliveryTile
                key='home-delivery'
                hasDeliveryOption={true}
                hasStock={false}
                isSelected={isHomeDelSelected}
                hasCncOption={true}
                hasCncStock={false}
                deliveryDescription={deliveryDescription}
                vendorName={vendorName}
                showLowStockWarning={false}
                isLoading={true}
                isDisabled={true}
                showOnlyMessage={false}
                onSelectionChange={createDeliveryModeHandler(true, false, updateModeOfAddToCart)}
            />,
            <ClickCollectTile
                key='click-collect'
                hasDeliveryOption={true}
                hasStock={false}
                isSelected={isCNCSelected}
                hasHdOption={true}
                hasHdStock={false}
                showLowStockWarning={false}
                isLoading={true}
                isDisabled={true}
                showOnlyMessage={false}
                onSelectionChange={createDeliveryModeHandler(false, true, updateModeOfAddToCart)}
            />
        );
        return [tiles];
    }

    // After loading: apply the delivery option and stock logic
    const bothOptionsExist = hasHomeDelOption && hasCncOption;
    const onlyHdOptionExists = hasHomeDelOption && !hasCncOption;
    const onlyCncOptionExists = !hasHomeDelOption && hasCncOption;

    // Case 1 & 2: Both HD and CnC options exist
    if (bothOptionsExist) {
        // Case 3: Both options exist but neither has stock - return empty (out of stock)
        if (!hasHomeDelStock && !hasCncStock) {
            return [tiles]; // Empty - will show out of stock section
        }

        // Show both tiles, disable the one without stock
        tiles.push(
            <HomeDeliveryTile
                key='home-delivery'
                hasDeliveryOption={hasHomeDelOption}
                hasStock={hasHomeDelStock}
                isSelected={isHomeDelSelected}
                hasCncOption={hasCncOption}
                hasCncStock={hasCncStock}
                deliveryDescription={deliveryDescription}
                vendorName={vendorName}
                showLowStockWarning={isHomeDelLowStock}
                isLoading={false}
                isDisabled={!hasHomeDelStock}
                showOnlyMessage={false}
                onSelectionChange={createDeliveryModeHandler(true, false, updateModeOfAddToCart)}
            />
        );
        tiles.push(
            <ClickCollectTile
                key='click-collect'
                hasDeliveryOption={hasCncOption}
                hasStock={hasCncStock}
                isSelected={isCNCSelected}
                hasHdOption={hasHomeDelOption}
                hasHdStock={hasHomeDelStock}
                showLowStockWarning={isCNCLowStock}
                isLoading={false}
                isDisabled={!hasCncStock}
                showOnlyMessage={false}
                onSelectionChange={createDeliveryModeHandler(false, true, updateModeOfAddToCart)}
            />
        );
    }

    // Case 4: Only HD option exists
    if (onlyHdOptionExists && hasHomeDelStock) {
        tiles.push(
            <HomeDeliveryTile
                key='home-delivery'
                hasDeliveryOption={hasHomeDelOption}
                hasStock={hasHomeDelStock}
                isSelected={isHomeDelSelected}
                hasCncOption={false}
                hasCncStock={false}
                deliveryDescription={deliveryDescription}
                vendorName={vendorName}
                showLowStockWarning={isHomeDelLowStock}
                isLoading={false}
                isDisabled={false}
                showOnlyMessage={true}
                onSelectionChange={createDeliveryModeHandler(true, false, updateModeOfAddToCart)}
            />
        );
    }

    // Case 5: Only CnC option exists
    if (onlyCncOptionExists && hasCncStock) {
        tiles.push(
            <ClickCollectTile
                key='click-collect'
                hasDeliveryOption={hasCncOption}
                hasStock={hasCncStock}
                isSelected={isCNCSelected}
                hasHdOption={false}
                hasHdStock={false}
                showLowStockWarning={isCNCLowStock}
                isLoading={false}
                isDisabled={false}
                showOnlyMessage={true}
                onSelectionChange={createDeliveryModeHandler(false, true, updateModeOfAddToCart)}
            />
        );
    }

    return [tiles];
};

/**
 * Click & Collect delivery tile component
 */
const ClickCollectTile: React.FC<IClickCollectTileProps> = ({
    hasDeliveryOption,
    hasStock,
    isSelected,
    hasHdOption,
    hasHdStock,
    showLowStockWarning,
    isLoading,
    isDisabled,
    showOnlyMessage,
    onSelectionChange
}) => {
    const tileDisabled = isLoading || isDisabled;
    return (
        <>
            {showOnlyMessage && <p className='only-cnc'>This item is available for Click & Collect only.</p>}
            <label className={`msc-cart-line__bopis-store-pickup ${isLoading ? 'loading' : ''} ${isDisabled ? 'disabled' : ''}`}>
                <input
                    className='delivery-radio'
                    type='radio'
                    name='shippingTypeHomeDel'
                    disabled={tileDisabled}
                    onChange={!tileDisabled ? onSelectionChange : undefined}
                    checked={isSelected}
                />
                <span className='msc-cart-line__custom-radio' />
                <div className='msc-cart-line__delivery-option'>
                    <div className='msc-cart-line__delivery-option__header'>
                        <p>Free Click & Collect</p>
                        <span className='msc-cart-line__delivery-option__header-icon' />
                    </div>
                    <div className='msc-cart-line__delivery-option__details'>
                        <>
                            {isDisabled ? (
                                <p className='out-of-stock-text'>This item is not available for Click & Collect</p>
                            ) : (
                                <p>
                                    Ready within <b>24 hours</b> if you order before <b>4pm</b>.
                                </p>
                            )}
                            {showLowStockWarning && !isDisabled && <p className='delivery-note'>Hurry, just a few left - order soon!</p>}
                        </>
                    </div>
                </div>
            </label>
        </>
    );
};

/**
 * Home Delivery tile component
 */
const HomeDeliveryTile: React.FC<IHomeDeliveryTileProps> = ({
    hasDeliveryOption,
    hasStock,
    isSelected,
    hasCncOption,
    hasCncStock,
    deliveryDescription,
    vendorName,
    showLowStockWarning,
    isLoading,
    isDisabled,
    showOnlyMessage,
    onSelectionChange
}) => {
    const tileDisabled = isLoading || isDisabled;
    return (
        <>
            {showOnlyMessage && <p className='only-hd'>This item is available for Home Delivery only.</p>}
            <label className={`msc-cart-line__bopis-shipping ${isLoading ? 'loading' : ''} ${isDisabled ? 'disabled' : ''}`}>
                <input
                    className='delivery-radio'
                    type='radio'
                    name='shippingTypeHomeDel'
                    disabled={tileDisabled}
                    onChange={!tileDisabled ? onSelectionChange : undefined}
                    checked={isSelected}
                />
                <span className='msc-cart-line__custom-radio' />
                <div className='msc-cart-line__delivery-option'>
                    <div className='msc-cart-line__delivery-option__header'>
                        <p>
                            Home Delivery <span>- dispatched by {vendorName || 'Dobbies'}</span>
                        </p>
                        <span className='msc-cart-line__delivery-option__header-icon'></span>
                    </div>
                    <div className='msc-cart-line__delivery-option__details'>
                        <>
                            {isDisabled ? (
                                <p className='out-of-stock-text'>This item is not available for Home Delivery</p>
                            ) : (
                                <p>
                                    Delivery: <span>{deliveryDescription || 'Standard - up to 5 working days'}</span>
                                </p>
                            )}
                            {showLowStockWarning && !isDisabled && <p className='delivery-note'>Hurry, just a few left - order soon!</p>}
                        </>
                    </div>
                </div>
            </label>
        </>
    );
};

interface IProductArrivalEstimateProps {
    actionContext: IActionContext;
    deliveryOptionsData?: ProductDeliveryOptions;
    loggedInZipCode?: string;
    alternateAddressBtn: React.ReactElement;
    deliveryOptions?: DeliveryOption[];
}

export const ProductArrivalEstimate = ({
    actionContext,
    loggedInZipCode,
    alternateAddressBtn,
    deliveryOptions
}: IProductArrivalEstimateProps) => {
    if (!deliveryOptions?.length) {
        return null;
    }

    return (
        <div className='estimated-arrival'>
            {deliveryOptions.map(opt => (
                <DeliveryEta
                    key={opt.Code}
                    actionContext={actionContext}
                    deliveryModeCode={opt.Code}
                    loggedInZipCode={loggedInZipCode}
                    alternateAddressBtn={alternateAddressBtn}
                />
            ))}
        </div>
    );
};

interface IDeliveryETAProps {
    actionContext: IActionContext;
    deliveryModeCode?: string;
    loggedInZipCode?: string;
    alternateAddressBtn: React.ReactElement;
}

const DeliveryEta = ({ deliveryModeCode, actionContext, loggedInZipCode, alternateAddressBtn }: IDeliveryETAProps) => {
    const code = deliveryModeCode || '';
    const config = actionContext.requestContext.app.config.additionalDeliveryMessages;
    const deliveryModes = actionContext.requestContext.app.config.deliveryModes;

    const DELIVERY_WITH_ETA = new Set(deliveryModes);

    const estDeliveryDate = React.useMemo(() => getDeliveryDateRange(new Date(), actionContext, code), [actionContext, code]);

    const msg = config?.find((c: { delMode: string }) => c.delMode === code)?.msg;
    const isLoggedInUser = actionContext.requestContext.user.isAuthenticated;
    const formattedMsg = isLoggedInUser && msg ? msg.replace(/\./, '') : msg;

    if (formattedMsg) {
        return (
            <p className='d-block additional-delivery-message'>
                <Msdyn365.RichTextComponent text={formattedMsg} />

                {loggedInZipCode && (
                    <>
                        {` to `}
                        <span>{loggedInZipCode.toUpperCase()}</span>
                    </>
                )}
                {loggedInZipCode && alternateAddressBtn}
            </p>
        );
    }

    if (!DELIVERY_WITH_ETA.has(code) || !estDeliveryDate?.maxDeliveryDate) {
        return null;
    }

    return (
        <p>
            Estimated arrival by:{' '}
            <span className='msc-cart-line__delivery-date'>
                {estDeliveryDate.maxDeliveryDate}
                {loggedInZipCode && (
                    <>
                        <span>to</span> {loggedInZipCode.toUpperCase()}
                    </>
                )}
                {loggedInZipCode && alternateAddressBtn}
            </span>
        </p>
    );
};

export const getEstimatedArrivalBy = async (
    productId: number,
    actionContext: IActionContext,
    loggedInZipCode?: string,
    productDeliveryOptions?: DeliveryOption[]
) => {
    const deliveryOptions = productDeliveryOptions;

    const additionalMsgConfig = actionContext.requestContext.app.config.additionalDeliveryMessages;
    const deliveryModes = actionContext.requestContext.app.config.deliveryModes;
    const DELIVERY_WITH_ETA = new Set(...deliveryModes);

    let msg = '';

    deliveryOptions?.forEach(mode => {
        const code = mode.Code || '';
        const est = getDeliveryDateRange(new Date(), actionContext, code);
        const custom = additionalMsgConfig?.find((c: { delMode: string }) => c.delMode === code)?.msg;

        if (custom) {
            msg = `<p>${custom}</p>`;
        } else if (DELIVERY_WITH_ETA.has(code)) {
            msg = `<p>Estimated arrival by: ${
                est?.maxDeliveryDate
                    ? `<span class='msc-cart-line__delivery-date'>${est.maxDeliveryDate}${
                          loggedInZipCode ? ' to ' + loggedInZipCode.toUpperCase() : ''
                      }</span>`
                    : ''
            }</p>`;
        }
    });

    return msg;
};
